import { TypedEventEmitter, setMaxListeners } from '@libp2p/interface';
import { AdaptiveTimeout } from '@libp2p/utils/adaptive-timeout';
import { Queue } from '@libp2p/utils/queue';
import drain from 'it-drain';
import { PROVIDERS_VALIDITY, REPROVIDE_CONCURRENCY, REPROVIDE_INTERVAL, REPROVIDE_MAX_QUEUE_SIZE, REPROVIDE_THRESHOLD } from './constants.js';
import { parseProviderKey, readProviderTime, timeOperationMethod } from './utils.js';
export class Reprovider extends TypedEventEmitter {
    log;
    reprovideQueue;
    maxQueueSize;
    datastore;
    timeout;
    reprovideTimeout;
    running;
    shutdownController;
    reprovideThreshold;
    contentRouting;
    datastorePrefix;
    addressManager;
    validity;
    interval;
    lock;
    peerId;
    constructor(components, init) {
        super();
        this.log = components.logger.forComponent(`${init.logPrefix}:reprovider`);
        this.peerId = components.peerId;
        this.reprovideQueue = new Queue({
            concurrency: init.concurrency ?? REPROVIDE_CONCURRENCY,
            metrics: components.metrics,
            metricName: `${init.metricsPrefix}_reprovide_queue`
        });
        this.reprovideTimeout = new AdaptiveTimeout({
            ...(init.timeout ?? {}),
            metrics: components.metrics,
            metricName: `${init.metricsPrefix}_reprovide_timeout_milliseconds`
        });
        this.datastore = components.datastore;
        this.addressManager = components.addressManager;
        this.datastorePrefix = `${init.datastorePrefix}/provider`;
        this.reprovideThreshold = init.threshold ?? REPROVIDE_THRESHOLD;
        this.maxQueueSize = init.maxQueueSize ?? REPROVIDE_MAX_QUEUE_SIZE;
        this.validity = init.validity ?? PROVIDERS_VALIDITY;
        this.interval = init.interval ?? REPROVIDE_INTERVAL;
        this.contentRouting = init.contentRouting;
        this.lock = init.lock;
        this.running = false;
        this.reprovide = timeOperationMethod(this.reprovide.bind(this), init.operationMetrics, 'PROVIDE');
    }
    start() {
        if (this.running) {
            return;
        }
        this.running = true;
        this.shutdownController = new AbortController();
        setMaxListeners(Infinity, this.shutdownController.signal);
        this.timeout = setTimeout(() => {
            this.cleanUp().catch(err => {
                this.log.error('error running reprovide/cleanup - %e', err);
            });
        }, this.interval);
    }
    stop() {
        this.running = false;
        this.reprovideQueue.clear();
        clearTimeout(this.timeout);
        this.shutdownController?.abort();
    }
    /**
     * Check all provider records. Delete them if they have expired, reprovide
     * them if the provider is us and the expiry is within the reprovide window.
     */
    async cleanUp() {
        const release = await this.lock.writeLock();
        try {
            this.safeDispatchEvent('reprovide:start');
            // Get all provider entries from the datastore
            for await (const entry of this.datastore.query({
                prefix: this.datastorePrefix
            })) {
                try {
                    // Add a delete to the batch for each expired entry
                    const { cid, peerId } = parseProviderKey(entry.key);
                    const created = readProviderTime(entry.value).getTime();
                    const expires = created + this.validity;
                    const now = Date.now();
                    const expired = now > expires;
                    this.log.trace('comparing: %d < %d = %s %s', created, now - this.validity, expired, expired ? '(expired)' : '');
                    // delete the record if it has expired
                    if (expired) {
                        await this.datastore.delete(entry.key);
                    }
                    // if the provider is us and we are within the reprovide threshold,
                    // reprovide the record
                    if (this.peerId.equals(peerId) && (now - expires) < this.reprovideThreshold) {
                        this.queueReprovide(cid)
                            .catch(err => {
                            this.log.error('could not reprovide %c - %e', cid, err);
                        });
                    }
                }
                catch (err) {
                    this.log.error('error processing datastore key %s - %e', entry.key, err.message);
                }
            }
            this.log('reprovide/cleanup successful');
        }
        finally {
            release();
            this.safeDispatchEvent('reprovide:end');
            if (this.running) {
                this.timeout = setTimeout(() => {
                    this.cleanUp().catch(err => {
                        this.log.error('error running re-provide - %e', err);
                    });
                }, this.interval);
            }
        }
    }
    async queueReprovide(cid) {
        if (!this.running) {
            return;
        }
        this.log.trace('waiting for queue capacity before adding %c to re-provide queue', cid);
        await this.reprovideQueue.onSizeLessThan(this.maxQueueSize);
        const existingJob = this.reprovideQueue.queue.find(job => job.options.cid.equals(cid));
        if (existingJob != null) {
            this.log.trace('not adding %c to re-provide queue - already in queue', cid);
            return existingJob.join();
        }
        this.log.trace('adding %c to re-provide queue', cid);
        this.reprovideQueue.add(async (options) => {
            options.signal?.throwIfAborted();
            if (!this.running) {
                return;
            }
            this.log.trace('re-providing %c', cid);
            // use adaptive timeout
            const signal = this.reprovideTimeout.getTimeoutSignal(options);
            try {
                // reprovide
                await this.reprovide(options.cid, options);
            }
            finally {
                this.reprovideTimeout.cleanUp(signal);
            }
            this.log.trace('re-provided %c', cid);
        }, {
            signal: this.shutdownController?.signal,
            cid
        })
            .catch(err => {
            this.log.error('could not re-provide key %c - %e', cid, err);
        });
    }
    async reprovide(cid, options) {
        // reprovide
        await drain(this.contentRouting.provide(cid, this.addressManager.getAddresses(), options));
    }
}
//# sourceMappingURL=reprovider.js.map